import type { StyleSpecification } from 'mapbox-gl';
// Presentation fit for Mapbox Standard, intentionally enlarged from real scale.
// +X follows F Street toward the alley; +Y points into the block.
// The front/left/right curbs meet the vector road edges. Keep this placement
// specific to the standard 3D map; it is not a satellite alignment.
export const SITE_ORIGIN: [number, number] = [-120.99189087378565, 37.6395979342522];
export const MODEL_ROTATION = -43.5;
export const PARCEL = [42.699432, 19.09572] as const;
// Measured from the GLB's curb vertices, not the legal parcel: the sidewalk
// extends west and south of the asset origin. glTF -Z is local +Y here.
export const CURB_BOUNDS = { west: -3.0925, front: -3.14, east: PARCEL[0], back: PARCEL[1] } as const;
// Fit the complete sidewalk-to-lot footprint to the three surrounding roads.
const widthScale = 53.1 / (CURB_BOUNDS.east - CURB_BOUNDS.west);
export const MODEL_SCALE: [number, number, number] = [widthScale, 29.7 / (CURB_BOUNDS.back - CURB_BOUNDS.front), widthScale];
export function localToLngLat(x: number, y: number): [number, number] {
  const a = MODEL_ROTATION * Math.PI / 180;
  x *= MODEL_SCALE[0];
  y *= MODEL_SCALE[1];
  const east = x * Math.cos(a) + y * Math.sin(a);
  const north = -x * Math.sin(a) + y * Math.cos(a);
  const metresPerDegree = Math.PI * 6371008.8 / 180;
  return [SITE_ORIGIN[0] + east / (metresPerDegree * Math.cos(SITE_ORIGIN[1] * Math.PI / 180)), SITE_ORIGIN[1] + north / metresPerDegree];
}
export const SITE_CENTER = localToLngLat((CURB_BOUNDS.west + CURB_BOUNDS.east) / 2, (CURB_BOUNDS.front + CURB_BOUNDS.back) / 2);
export const replacementBoundary = {
  type: 'Feature' as const, properties: {}, geometry: { type: 'Polygon' as const,
    coordinates: [[[CURB_BOUNDS.west, CURB_BOUNDS.front], [CURB_BOUNDS.east, CURB_BOUNDS.front], [CURB_BOUNDS.east, CURB_BOUNDS.back], [CURB_BOUNDS.west, CURB_BOUNDS.back], [CURB_BOUNDS.west, CURB_BOUNDS.front]].map(([x, y]) => localToLngLat(x, y))],
  },
};
// Standard's pixel-width roads narrow in metres as you zoom in. This fixed
// ground surface bridges the three street-facing curbs to those road bands,
// rather than resizing or moving the building to chase the basemap styling.
const streetApron = {
  type: 'Feature' as const, properties: {}, geometry: { type: 'Polygon' as const,
    coordinates: [[
      [CURB_BOUNDS.west - 3 / MODEL_SCALE[0], CURB_BOUNDS.front - 3 / MODEL_SCALE[1]],
      [CURB_BOUNDS.east + 3 / MODEL_SCALE[0], CURB_BOUNDS.front - 3 / MODEL_SCALE[1]],
      [CURB_BOUNDS.east + 3 / MODEL_SCALE[0], CURB_BOUNDS.back],
      [CURB_BOUNDS.west - 3 / MODEL_SCALE[0], CURB_BOUNDS.back],
      [CURB_BOUNDS.west - 3 / MODEL_SCALE[0], CURB_BOUNDS.front - 3 / MODEL_SCALE[1]],
    ].map(([x, y]) => localToLngLat(x, y))],
  },
};
export function modelStyle(modelUrl = '/models/1401-f-st.glb'): StyleSpecification {
  return {
    version: 8,
    imports: [{ id: 'basemap', url: 'mapbox://styles/mapbox/standard', config: { lightPreset: 'day', show3dObjects: true, showPointOfInterestLabels: false, showTransitLabels: false } }],
    sources: {
      '1401-boundary': { type: 'geojson', data: replacementBoundary },
      '1401-street-apron': { type: 'geojson', data: streetApron },
      // A non-tiled source retains the exact geographic anchor at every zoom;
      // GeoJSON point tiling rounds the anchor onto progressively coarser grids.
      '1401-asset': { type: 'model', models: { 'aps-hq': { uri: modelUrl, position: SITE_ORIGIN } } },
    },
    layers: [
      { id: '1401-replacement', type: 'clip', source: '1401-boundary', layout: { 'clip-layer-types': ['model', 'symbol'], 'clip-layer-scope': ['basemap'] } },
      { id: '1401-street-surface', type: 'fill', source: '1401-street-apron', slot: 'middle',
        paint: { 'fill-color': 'hsl(224, 25%, 80%)', 'fill-emissive-strength': 0.4 },
      },
      { id: '1401-model', type: 'model', source: '1401-asset', slot: 'middle',
        paint: { 'model-rotation': [0, 0, MODEL_ROTATION], 'model-scale': MODEL_SCALE, 'model-translation': [0, 0, .03], 'model-color-mix-intensity': 0, 'model-opacity': 1, 'model-cast-shadows': true, 'model-receive-shadows': true, 'model-emissive-strength': 0,
          // Keep our site visible even where the basemap simplifies its roads
          // and removes buildings. Apparent size still follows its metre size.
          'model-cutoff-fade-range': 0, 'model-line-cutout-mode': 'disabled',
        },
      },
    ],
  };
}
